import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../context/AuthContext';
import {
  Users,
  ShoppingBag,
  Package,
  Handshake,
  Crown,
  Loader2,
  TrendingUp,
  Flag,
  AlertTriangle,
  BarChart3,
  ArrowRight,
  Shield,
  Radar,
  Gavel,
  Ban
} from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats();
  }, [token]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2A4A6F] rounded-xl p-6 mb-6 text-white">
          <h1 className="text-2xl font-bold mb-2">Welcome to WANTD Admin</h1>
          <p className="text-white/70">Monitor and manage your platform from here.</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_users || 0}</p>
                <p className="text-xs text-gray-500">Total Users</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_wtb || 0}</p>
                <p className="text-xs text-gray-500">Want Listings</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_inventory || 0}</p>
                <p className="text-xs text-gray-500">Inventory Items</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Handshake className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_matches || 0}</p>
                <p className="text-xs text-gray-500">Matches</p>
              </div>
            </div>
          </div>
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-lg font-bold text-gray-900">{stats?.active_users_24h || 0}</p>
            <p className="text-xs text-gray-500">Active Today</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-lg font-bold text-[#C9956C]">{stats?.premium_users || 0}</p>
            <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
              <Crown className="w-3 h-3" /> Premium Users
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-lg font-bold text-purple-600">{stats?.admin_users || 0}</p>
            <p className="text-xs text-gray-500">Admin Users</p>
          </div>
        </div>

        {/* Action Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Moderation Overview */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-500" />
                <h3 className="font-bold text-gray-900">Moderation</h3>
              </div>
              {stats?.pending_reports > 0 && (
                <span className="px-2 py-1 bg-red-100 text-red-600 rounded-full text-xs font-bold">
                  {stats.pending_reports}
                </span>
              )}
            </div>
            {stats?.pending_reports > 0 ? (
              <p className="text-sm text-gray-500 mb-3">
                <AlertTriangle className="w-4 h-4 inline mr-1 text-amber-500" />
                {stats.pending_reports} report{stats.pending_reports !== 1 ? 's' : ''} awaiting review
              </p>
            ) : (
              <p className="text-sm text-gray-500 mb-3">No pending reports</p>
            )}
            <div className="flex flex-wrap gap-2">
              <Link to="/admin/moderation/reports">
                <Button size="sm" variant="outline" className="gap-1 text-xs">
                  <Flag className="w-3.5 h-3.5" /> Community Reports
                </Button>
              </Link>
              <Link to="/admin/moderation/detections">
                <Button size="sm" variant="outline" className="gap-1 text-xs">
                  <Radar className="w-3.5 h-3.5" /> Auto Detections
                </Button>
              </Link>
              <Link to="/admin/moderation/discipline">
                <Button size="sm" variant="outline" className="gap-1 text-xs">
                  <Gavel className="w-3.5 h-3.5" /> Discipline
                  {((stats?.suspended_users || 0) + (stats?.banned_users || 0)) > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 bg-red-100 text-red-600 rounded-full text-xs font-bold">
                      {(stats?.suspended_users || 0) + (stats?.banned_users || 0)}
                    </span>
                  )}
                </Button>
              </Link>
            </div>
          </div>

          {/* Quick Analytics */}
          <Link to="/admin/analytics" className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:border-purple-300 transition-colors">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                <h3 className="font-bold text-gray-900">Analytics</h3>
              </div>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-sm text-gray-500">
              View detailed platform metrics, trends, and revenue data.
            </p>
            <div className="mt-3 flex items-center text-blue-600 text-sm font-medium">
              View Analytics <ArrowRight className="w-4 h-4 ml-1" />
            </div>
          </Link>
        </div>
      </div>
    </AdminLayout>
  );
}
